// src/features/atividade/fonteGps.js
//
// A porta única para o GPS e para a tela acesa. Nenhuma outra parte da feature
// importa `@capgo/background-geolocation` nem `@capacitor-community/keep-awake`
// — quem quiser posição pede aqui.
//
// POR QUE UMA PORTA. Duas razões práticas:
//  1. NO NAVEGADOR o plugin não existe. Sem um caminho web, a feature inteira
//     só poderia ser vista compilando para o aparelho, e o ciclo de ajuste de
//     layout (que é a maior parte do trabalho) passaria a custar um build
//     nativo por iteração. Aqui o web cai em `navigator.geolocation`, que dá
//     posição de verdade no `npm start` e no Chrome com localização simulada.
//  2. O import do plugin é DINÂMICO. Ele arrasta código nativo e o boot do app
//     não deve pagar por uma feature que a maioria das sessões não abre.
//
// Os plugins são carregados sob demanda e, se falharem, a chamada vira no-op
// silencioso — a mesma regra de `design/feedback/haptics.js`.

import { Capacitor } from '@capacitor/core';
import { finito } from './geo.js';

const nativo = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

// ── Carga sob demanda ────────────────────────────────────────────────────────

/**
 * O QUE `registerPlugin` DEVOLVE É UM PROXY QUE RESPONDE A TUDO.
 *
 * O objeto de um plugin Capacitor é um `Proxy` cujo `get` devolve uma função
 * para QUALQUER propriedade pedida (`@capacitor/core`, `registerPlugin`) —
 * inclusive `then`. Isso o torna um "thenable", e o runtime de Promises trata
 * thenable como promessa: devolver o proxy de dentro de um `.then()` ou de uma
 * função `async` faz o próprio JS chamar `plugin.then(resolve, reject)`, que o
 * Capacitor traduz numa chamada nativa que não existe. Era exatamente isso o
 *
 *     Unhandled Promise Rejection: Error:
 *     "BackgroundGeolocation.then()" is not implemented on ios
 *
 * — e, como a promessa rejeitava, `await plugin()` nunca entregava o plugin:
 * o GPS não ligava no aparelho, só no navegador.
 *
 * A saída é nunca deixar o proxy cru atravessar uma promessa. `envolver`
 * copia os métodos que usamos para um objeto comum, que não tem `then` e
 * portanto passa por `await` intacto.
 */
function envolver(proxy, metodos) {
  if (!proxy) return null;
  const alvo = {};
  for (const nome of metodos) {
    alvo[nome] = (...args) => proxy[nome](...args);
  }
  return alvo;
}

const METODOS_GEO = ['checkPermissions', 'requestPermissions', 'openSettings', 'start', 'stop'];
const METODOS_AWAKE = ['keepAwake', 'allowSleep', 'isSupported', 'isKeptAwake'];

let moduloGeo = null;
let carregandoGeo = null;

async function plugin() {
  if (!nativo()) return null;
  if (moduloGeo) return moduloGeo;
  if (!carregandoGeo) {
    carregandoGeo = import('@capgo/background-geolocation')
      .then((m) => {
        moduloGeo = envolver(m.BackgroundGeolocation || m.default || null, METODOS_GEO);
        return moduloGeo;
      })
      .catch(() => null);
  }
  return carregandoGeo;
}

let moduloAwake = null;
let carregandoAwake = null;

async function awake() {
  if (!nativo()) return null;
  if (moduloAwake) return moduloAwake;
  if (!carregandoAwake) {
    carregandoAwake = import('@capacitor-community/keep-awake')
      .then((m) => {
        moduloAwake = envolver(m.KeepAwake || m.default || null, METODOS_AWAKE);
        return moduloAwake;
      })
      .catch(() => null);
  }
  return carregandoAwake;
}

// ── Normalização ─────────────────────────────────────────────────────────────

/**
 * Traduz a posição do plugin (ou do navegador) para a forma que o `geo.js`
 * entende. Um formato só no app inteiro.
 *
 * `t` vem do PROVEDOR (`time` / `timestamp`), não de `Date.now()`: o relógio do
 * aparelho pode ser ajustado no meio da atividade, e com ele o pace daria
 * salto. Só cai no relógio local quando o provedor não informa.
 */
function normalizar(bruto) {
  if (!bruto) return null;
  const c = bruto.coords || bruto;
  if (!finito(c.latitude) || !finito(c.longitude)) return null;

  // `finito` e não `Number.isFinite(Number(x))`: o provedor manda `null` no
  // que não sabe, e `Number(null)` é 0. Sem a guarda certa, altitude ausente
  // vira "nível do mar", precisão ausente vira "0 m, perfeita", e velocidade
  // ausente vira "parado" — que dispara o auto-pause em quem está correndo.
  return {
    lat: Number(c.latitude),
    lng: Number(c.longitude),
    alt: finito(c.altitude) ? Number(c.altitude) : null,
    acc: finito(c.accuracy) ? Number(c.accuracy) : null,
    v: finito(c.speed) ? Number(c.speed) : null,
    t: Number(bruto.time) || Number(bruto.timestamp) || Date.now(),
    // `simulated` só existe no nativo. Não bloqueia nada — quem usa mock
    // location em desenvolvimento precisa que funcione — mas viaja até o
    // documento para uma auditoria futura ter como perguntar.
    sim: bruto.simulated === true,
  };
}

// ── Permissão ────────────────────────────────────────────────────────────────

export const PERMISSAO = {
  CONCEDIDA: 'concedida',
  NEGADA: 'negada',
  PENDENTE: 'pendente',
  INDISPONIVEL: 'indisponivel',
};

function traduzirEstado(estado) {
  if (estado === 'granted' || estado === 'always' || estado === 'when_in_use') {
    return PERMISSAO.CONCEDIDA;
  }
  if (estado === 'denied') return PERMISSAO.NEGADA;
  return PERMISSAO.PENDENTE;
}

/**
 * Estado da permissão SEM abrir diálogo. É o que a pré-atividade consulta ao
 * montar: perguntar antes de a pessoa tocar em "Iniciar" queimaria o único
 * diálogo que o iOS concede por instalação.
 *
 * @returns {Promise<string>} um valor de PERMISSAO
 */
export async function conferirPermissao() {
  if (!nativo()) {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      return PERMISSAO.INDISPONIVEL;
    }
    try {
      const status = await navigator.permissions?.query({ name: 'geolocation' });
      if (status) return traduzirEstado(status.state === 'prompt' ? 'prompt' : status.state);
    } catch {
      /* Safari antigo não tem Permissions API para geolocation */
    }
    return PERMISSAO.PENDENTE;
  }

  const bg = await plugin();
  if (!bg) return PERMISSAO.INDISPONIVEL;
  try {
    const status = await bg.checkPermissions();
    return traduzirEstado(status?.location);
  } catch {
    return PERMISSAO.PENDENTE;
  }
}

/**
 * Pede a permissão, com diálogo. Só em resposta a um toque.
 *
 * Pede `location` e `notification` — a segunda porque o Android 13+ exige
 * POST_NOTIFICATIONS para o serviço de primeiro plano mostrar a notificação, e
 * sem notificação o serviço não roda. NÃO pede `backgroundLocation`: o app não
 * declara ACCESS_BACKGROUND_LOCATION (ver B1).
 *
 * @returns {Promise<string>} um valor de PERMISSAO
 */
export async function pedirPermissao() {
  if (!nativo()) {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      return PERMISSAO.INDISPONIVEL;
    }
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        () => resolve(PERMISSAO.CONCEDIDA),
        (e) => resolve(e?.code === 1 ? PERMISSAO.NEGADA : PERMISSAO.PENDENTE),
        { enableHighAccuracy: true, timeout: 15000 }
      );
    });
  }

  const bg = await plugin();
  if (!bg) return PERMISSAO.INDISPONIVEL;
  try {
    const status = await bg.requestPermissions({ permissions: ['location', 'notification'] });
    return traduzirEstado(status?.location);
  } catch {
    return PERMISSAO.NEGADA;
  }
}

/** Abre os ajustes do sistema — o botão do estado "permissão negada". */
export async function abrirAjustes() {
  const bg = await plugin();
  if (!bg) return;
  try {
    await bg.openSettings();
  } catch {
    /* silencioso: o botão não pode derrubar a tela */
  }
}

// ── Assinatura de posição ────────────────────────────────────────────────────

let pararWeb = null;

/**
 * Liga o GPS e chama `aoPonto` a cada posição nova.
 *
 * `distanceFilter: 0` de propósito: o filtro do plugin é por distância e
 * suprimiria atualizações com a pessoa parada — mas é justamente a sequência
 * de pontos parados que o auto-pause precisa ver para disparar. O filtro de
 * verdade é o `aceitarPonto` do geo.js, que sabe da precisão e da modalidade.
 *
 * O título e o texto são os da notificação do serviço de primeiro plano no
 * Android ENQUANTO a gravação não começou: o GPS liga na pré-atividade (para o
 * mapa e o sinal), e dizer "gravando" ali seria mentira. Quando a gravação
 * começa, o AtividadeNotificacaoPlugin redesenha a mesma notificação com
 * cronômetro, números e botões (ver liveActivity.js).
 *
 * @param {object} opcoes `{ aoPonto, aoErro, tituloNotificacao, textoNotificacao }`
 * @returns {Promise<Function>} função que desliga
 */
export async function assinarPosicao({
  aoPonto,
  aoErro,
  tituloNotificacao = 'GPS ligado',
  textoNotificacao = 'Pronto para gravar sua atividade.',
} = {}) {
  const entregar = (bruto) => {
    const p = normalizar(bruto);
    if (p) aoPonto?.(p);
  };

  if (!nativo()) {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      aoErro?.(new Error('Este navegador não tem GPS.'));
      return () => {};
    }
    const id = navigator.geolocation.watchPosition(entregar, (e) => aoErro?.(e), {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 30000,
    });
    pararWeb = () => navigator.geolocation.clearWatch(id);
    return pararWeb;
  }

  const bg = await plugin();
  if (!bg) {
    aoErro?.(new Error('Plugin de localização indisponível.'));
    return () => {};
  }

  await bg.start(
    {
      // `backgroundMessage` definido é o que faz o plugin entregar posição com
      // o app em segundo plano. Sem ele, só em primeiro plano — e a atividade
      // pararia ao bloquear a tela.
      backgroundTitle: tituloNotificacao,
      backgroundMessage: textoNotificacao,
      // A permissão já foi pedida pela pré-atividade, em resposta a um toque.
      // Deixar o plugin pedir aqui abriria o diálogo no meio da transição.
      requestPermissions: false,
      // Posição velha não entra: o pace seria calculado sobre um instante que
      // já passou e daria salto no primeiro fix.
      stale: false,
      distanceFilter: 0,
    },
    (posicao, erro) => {
      if (erro) {
        aoErro?.(erro);
        return;
      }
      entregar(posicao);
    }
  );

  return async () => {
    try {
      await bg.stop();
    } catch {
      /* já parado */
    }
  };
}

/** Desliga qualquer assinatura que tenha ficado de pé (saída de emergência). */
export async function pararTudo() {
  if (pararWeb) {
    pararWeb();
    pararWeb = null;
  }
  const bg = await plugin();
  if (!bg) return;
  try {
    await bg.stop();
  } catch {
    /* já parado */
  }
}

// ── Tela acesa ───────────────────────────────────────────────────────────────

/**
 * Mantém a tela acesa durante a gravação (brief §6.3).
 *
 * Só enquanto grava, e liberada no fim: deixar ligada por engano descarrega a
 * bateria de quem esqueceu o app aberto.
 */
export async function manterTelaAcesa(ligar) {
  const ka = await awake();
  if (!ka) return;
  try {
    if (ligar) await ka.keepAwake();
    else await ka.allowSleep();
  } catch {
    /* silencioso: tela apagando não pode derrubar a gravação */
  }
}

export default {
  conferirPermissao,
  pedirPermissao,
  abrirAjustes,
  assinarPosicao,
  pararTudo,
  manterTelaAcesa,
  PERMISSAO,
};
